/**
 * React hook for group poster management
 */
import { useState, useCallback } from 'react';
import { useFirebaseServices } from '../app/providers/FirebaseProvider';
import { useStorageService } from './useStorageService';
import {
  updateGroupPoster,
  removeGroupPoster,
} from '../features/groups/api/groupRepository';
import type {
  UploadProgress,
  UploadResult,
  StorageError,
  PosterAsset,
} from '../lib/storage';
import { validatePosterFile, validateAltText } from '../lib/storage';

interface PosterUploadState {
  isUploading: boolean;
  progress: UploadProgress | null;
  error: StorageError | null;
  result: UploadResult | null;
}

interface UseGroupPosterManagementProps {
  groupId: string;
}

export const useGroupPosterManagement = ({ groupId }: UseGroupPosterManagementProps) => {
  const { firestore } = useFirebaseServices();
  const storageService = useStorageService();
  const [uploadState, setUploadState] = useState<PosterUploadState>({
    isUploading: false,
    progress: null,
    error: null,
    result: null,
  });

  /**
   * Upload a poster for the group
   */
  const uploadPoster = useCallback(
    async (file: File, altText?: string): Promise<UploadResult | null> => {
      if (!storageService || !firestore) {
        throw new Error('Storage service or Firestore not available');
      }

      setUploadState({
        isUploading: true,
        progress: null,
        error: null,
        result: null,
      });

      try {
        // Validate file first
        validatePosterFile(file);
        validateAltText(altText);

        const result = await storageService.uploadGroupPosterImage({
          groupId,
          file,
          altText,
          onProgress: progress => {
            setUploadState(prev => ({
              ...prev,
              progress,
            }));
          },
          onError: error => {
            setUploadState(prev => ({
              ...prev,
              error,
            }));
          },
        });

        console.log('✅ Storage upload complete:', {
          url: result.url,
          storagePath: result.storagePath,
          groupId,
        });

        // Update group document with poster information
        console.log('📝 Updating Firestore group document with poster info...');
        await updateGroupPoster(firestore, groupId, {
          posterUrl: result.url,
          posterStoragePath: result.storagePath,
          posterAlt: altText,
        });
        console.log('✅ Firestore update complete!');

        setUploadState({
          isUploading: false,
          progress: null,
          error: null,
          result,
        });

        return result;
      } catch (error) {
        console.error('❌ Poster upload failed:', error);
        if (error instanceof Error) {
          console.error('Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack,
          });
        }
        const storageError = error as StorageError;
        setUploadState({
          isUploading: false,
          progress: null,
          error: storageError,
          result: null,
        });
        throw error;
      }
    },
    [storageService, firestore, groupId],
  );

  /**
   * Remove the poster from the group
   */
  const removePoster = useCallback(
    async (storagePath: string): Promise<void> => {
      if (!storageService || !firestore) {
        throw new Error('Storage service or Firestore not available');
      }

      try {
        // Remove from storage
        await storageService.deletePosterImage(storagePath);

        // Remove from group document
        await removeGroupPoster(firestore, groupId);
      } catch (error) {
        console.error('Failed to remove poster:', error);
        throw error;
      }
    },
    [storageService, firestore, groupId],
  );

  /**
   * Get poster asset information
   */
  const getPosterAsset = useCallback(
    async (storagePath: string): Promise<PosterAsset | null> => {
      if (!storageService) {
        throw new Error('Storage service not available');
      }

      return storageService.getPosterAsset(storagePath);
    },
    [storageService],
  );

  /**
   * List all posters for the group
   */
  const listGroupPosters = useCallback(async (): Promise<PosterAsset[]> => {
    if (!storageService) {
      throw new Error('Storage service not available');
    }

    return storageService.listGroupPosters(groupId);
  }, [storageService, groupId]);

  /**
   * Update poster metadata
   */
  const updatePosterMetadata = useCallback(
    async (storagePath: string, altText: string): Promise<void> => {
      if (!storageService || !firestore) {
        throw new Error('Storage service or Firestore not available');
      }

      validateAltText(altText);

      // Update storage metadata
      await storageService.updatePosterMetadata(storagePath, {
        customMetadata: {
          altText,
        },
      });

      // Update group document - only update alt text and storage path
      await updateGroupPoster(firestore, groupId, {
        posterStoragePath: storagePath,
        posterAlt: altText,
      });
    },
    [storageService, firestore, groupId],
  );

  /**
   * Reset upload state
   */
  const resetUploadState = useCallback(() => {
    setUploadState({
      isUploading: false,
      progress: null,
      error: null,
      result: null,
    });
  }, []);

  return {
    // State
    uploadState,

    // Actions
    uploadPoster,
    removePoster,
    getPosterAsset,
    listGroupPosters,
    updatePosterMetadata,
    resetUploadState,

    // Computed
    isUploading: uploadState.isUploading,
    hasError: uploadState.error !== null,
    hasResult: uploadState.result !== null,
    progress: uploadState.progress,
    error: uploadState.error,
    result: uploadState.result,
  };
};
